import { createMemo, createSignal, For, onCleanup, onMount, Show, type JSX } from 'solid-js';
import { gradientActionLabel } from '../lib/gradientSession';
import {
  GRADIENT_PRESETS,
  addGradientStop,
  clampLevel,
  moveGradientStop,
  removeGradientStop,
  type GradientStop,
  type RunDistribution,
} from '../lib/panelElements';
import { TEST_IDS } from '../testIds';

interface GradientEditorProps {
  stops: GradientStop[];
  mirror: boolean;
  disabled?: boolean;
  /** The selected pixels grouped per run, for the preview drawn under the stops bar. */
  rows: RunDistribution[];
  /** Whether this selection has already had the gradient sent, which is what the action reads. */
  applied: boolean;
  onPreview: (stops: GradientStop[], mirror: boolean) => void;
  onApply: (stops: GradientStop[], mirror: boolean) => void;
  busy?: boolean;
}

interface StopDrag {
  id: string;
  pointerId: number;
  target: HTMLElement;
  startClientX: number;
  startPosition: number;
  moved: boolean;
}

interface BarPress {
  pointerId: number;
  startClientX: number;
  startClientY: number;
  moved: boolean;
}

const DRAG_THRESHOLD = 3;

function stopColor(level: number): string {
  const brightness = Math.round(20 + clampLevel(level) * 235);
  return `rgb(${brightness} ${Math.round(brightness * .93)} ${Math.round(brightness * .75)})`;
}

/**
 * The multi-stop gradient editor.
 *
 * The bar is the CURRENT SELECTION from start to end in room-path order — that is what "across the
 * selection" means, and multi-run selections concatenate in that order. Under the bar the same
 * selection is drawn one row per run, so the operator can see where each run falls on the axis they
 * are editing. Editing previews in the room but persists nothing. Apply sends the finished per-pixel
 * values once and leaves the editor open on the same selection, so the gradient can be adjusted and
 * sent again; Undo is the way back.
 */
export function GradientEditor(props: GradientEditorProps): JSX.Element {
  const [activeStopId, setActiveStopId] = createSignal(props.stops[0]?.id ?? 'a');
  const [drag, setDrag] = createSignal<StopDrag>();
  const [barPress, setBarPress] = createSignal<BarPress>();
  let bar!: HTMLDivElement;

  const activeStop = createMemo(() => props.stops.find((stop) => stop.id === activeStopId()) ?? props.stops[0]);
  const totalSelected = createMemo(() => props.rows.reduce((total, row) => total + row.selected, 0));
  const background = createMemo(() => {
    const points = [...props.stops]
      .sort((left, right) => left.position - right.position)
      .map((stop) => `${stopColor(stop.level)} ${Math.round(stop.position * 1000) / 10}%`);
    return `linear-gradient(90deg, ${points.join(', ')})`;
  });

  function barGeometry(): { left: number; width: number } {
    const bounds = bar.getBoundingClientRect();
    // Absolutely-positioned stops resolve percentages against the padding box. Use that exact box for
    // pointer math too; including the two borders produces a visible ~1 px release error near an end.
    return { left: bounds.left + bar.clientLeft, width: Math.max(1, bar.clientWidth) };
  }

  function positionFor(clientX: number): number {
    const geometry = barGeometry();
    return clampLevel((clientX - geometry.left) / geometry.width);
  }

  function addStop(clientX: number): void {
    const id = `stop-${crypto.randomUUID()}`;
    const stops = addGradientStop(props.stops, positionFor(clientX), id);
    setActiveStopId(id);
    props.onPreview(stops, props.mirror);
  }

  function beginBarPress(event: PointerEvent): void {
    if (props.disabled || event.button !== 0) return;
    event.preventDefault();
    bar.setPointerCapture(event.pointerId);
    setBarPress({
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      moved: false,
    });
  }

  function moveBarPress(event: PointerEvent): void {
    const current = barPress();
    if (!current || current.pointerId !== event.pointerId) return;
    event.preventDefault();
    const moved = current.moved
      || Math.hypot(event.clientX - current.startClientX, event.clientY - current.startClientY) >= DRAG_THRESHOLD;
    if (moved !== current.moved) setBarPress({ ...current, moved });
  }

  function finishBarPress(event: PointerEvent, cancelled = false): void {
    const current = barPress();
    if (!current || current.pointerId !== event.pointerId) return;
    if (!cancelled && !current.moved) addStop(current.startClientX);
    setBarPress(undefined);
  }

  function startStopDrag(event: PointerEvent, stop: GradientStop): void {
    if (props.disabled || event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    setActiveStopId(stop.id);
    const target = event.currentTarget as HTMLElement;
    if (event.altKey) {
      const ordered = [...props.stops].sort((left, right) => left.position - right.position);
      const endpoint = ordered[0]?.id === stop.id || ordered.at(-1)?.id === stop.id;
      if (!endpoint && props.stops.length > 2) {
        props.onPreview(removeGradientStop(props.stops, stop.id), props.mirror);
        setActiveStopId(ordered.find((candidate) => candidate.id !== stop.id)?.id ?? 'a');
      }
      return;
    }
    target.setPointerCapture(event.pointerId);
    setDrag({
      id: stop.id,
      pointerId: event.pointerId,
      target,
      startClientX: event.clientX,
      startPosition: stop.position,
      moved: false,
    });
  }

  function moveStopDrag(event: PointerEvent): void {
    const current = drag();
    if (!current || current.pointerId !== event.pointerId) return;
    event.preventDefault();
    const moved = current.moved || Math.abs(event.clientX - current.startClientX) >= DRAG_THRESHOLD;
    if (!moved) return;
    const width = barGeometry().width;
    const position = clampLevel(current.startPosition + (event.clientX - current.startClientX) / width);
    props.onPreview(moveGradientStop(props.stops, current.id, position), props.mirror);
    if (!current.moved) setDrag({ ...current, moved: true });
  }

  function finishStopDrag(event: PointerEvent, cancelled = false): void {
    const current = drag();
    if (!current || current.pointerId !== event.pointerId) return;
    event.preventDefault();
    if (cancelled && current.moved) {
      props.onPreview(moveGradientStop(props.stops, current.id, current.startPosition), props.mirror);
    } else if (current.moved) {
      const width = barGeometry().width;
      const position = clampLevel(current.startPosition + (event.clientX - current.startClientX) / width);
      props.onPreview(moveGradientStop(props.stops, current.id, position), props.mirror);
    }
    setDrag(undefined);
  }

  /**
   * Browsers do not agree on whether leaving the window produces pointercancel, lostpointercapture, or
   * only blur. Fold all three onto one cancellation edge so a stop can never stay armed after the mouse,
   * trackpad or finger has gone away.
   */
  function cancelPointerLifecycle(): void {
    const currentDrag = drag();
    if (currentDrag) {
      if (currentDrag.moved) {
        props.onPreview(moveGradientStop(props.stops, currentDrag.id, currentDrag.startPosition), props.mirror);
      }
      setDrag(undefined);
      if (currentDrag.target.hasPointerCapture(currentDrag.pointerId)) {
        currentDrag.target.releasePointerCapture(currentDrag.pointerId);
      }
    }
    const currentBarPress = barPress();
    if (currentBarPress) {
      setBarPress(undefined);
      if (bar?.hasPointerCapture(currentBarPress.pointerId)) bar.releasePointerCapture(currentBarPress.pointerId);
    }
  }

  function moveActivePointer(event: PointerEvent): void {
    if (drag()) moveStopDrag(event);
    else if (barPress()) moveBarPress(event);
  }

  function finishActivePointer(event: PointerEvent, cancelled = false): void {
    if (drag()) finishStopDrag(event, cancelled);
    else if (barPress()) finishBarPress(event, cancelled);
  }

  function cancelActivePointer(event: PointerEvent): void {
    finishActivePointer(event, true);
  }

  onMount(() => {
    // Keep the lifecycle above the moving stop nodes. Pointer capture remains on the pressed stop,
    // while the window edge guarantees that a release over a sibling or beyond the bar still commits.
    window.addEventListener('pointermove', moveActivePointer);
    window.addEventListener('pointerup', finishActivePointer);
    window.addEventListener('pointercancel', cancelActivePointer);
    window.addEventListener('blur', cancelPointerLifecycle);
  });
  onCleanup(() => {
    window.removeEventListener('pointermove', moveActivePointer);
    window.removeEventListener('pointerup', finishActivePointer);
    window.removeEventListener('pointercancel', cancelActivePointer);
    window.removeEventListener('blur', cancelPointerLifecycle);
  });

  function updateActiveLevel(level: number, commit: boolean): void {
    const stop = activeStop();
    if (!stop) return;
    const next = moveGradientStop(props.stops, stop.id, stop.position, level);
    props.onPreview(next, props.mirror);
    void commit;
  }

  function applyPreset(name: string): void {
    const next = GRADIENT_PRESETS[name].map((stop) => ({ ...stop, id: `${stop.id}-${crypto.randomUUID()}` }));
    setActiveStopId(next[0].id);
    props.onPreview(next, false);
  }

  return (
    <div class="gradient-editor" data-testid={TEST_IDS.gradientEditor}>
      <div class="gradient-editor-heading">
        <span><strong>Gradient</strong><small>{totalSelected().toLocaleString()} pixels, start to end in room order</small></span>
        <label class="mirror-toggle" title="Folds the gradient symmetrically over the selection."><input type="checkbox" checked={props.mirror} disabled={props.disabled} onChange={(event) => {
          props.onPreview(props.stops, event.currentTarget.checked);
        }} /> Mirror</label>
      </div>
      <div
        class="gradient-bar"
        ref={bar}
        style={{ background: background() }}
        onPointerDown={beginBarPress}
        data-testid={TEST_IDS.gradientBar}
        aria-label="Gradient stops"
      >
        <For each={props.stops.map((stop) => stop.id)}>
          {(stopId) => {
            // Key the DOM node by the durable stop ID, not the stop object. Preview updates replace
            // value objects; replacing the button too would discard its pointer capture mid-drag.
            const stop = createMemo(() => props.stops.find((candidate) => candidate.id === stopId)!);
            return (
              <button
                type="button"
                class="gradient-stop"
                classList={{ active: activeStopId() === stopId }}
                style={{ left: `${stop().position * 100}%`, '--stop-level': String(stop().level) }}
                disabled={props.disabled}
                aria-label={`Gradient stop ${Math.round(stop().position * 100)}%, level ${Math.round(stop().level * 100)}%`}
                data-stop-id={stopId}
                data-dragging={drag()?.id === stopId ? 'true' : 'false'}
                onPointerDown={(event) => startStopDrag(event, stop())}
                onClick={(event) => { event.preventDefault(); event.stopPropagation(); }}
              ><i /></button>
            );
          }}
        </For>
      </div>
      <Show when={props.rows.length}>
        <div class="gradient-axis-rows" data-testid={TEST_IDS.gradientAxisRows}>
          <For each={props.rows}>
            {(row) => (
              <div
                class="gradient-axis-row"
                data-run-id={row.runId}
                style={{
                  '--start': String(row.offset / Math.max(1, totalSelected())),
                  '--span': String(row.selected / Math.max(1, totalSelected())),
                }}
              >
                <i /><span>{row.name}</span>
              </div>
            )}
          </For>
        </div>
      </Show>
      <p class="gradient-delete-hint">Click to add a stop · drag to move · ⌥-click to remove</p>
      <Show when={activeStop()}>
        {(stop) => (
          <div class="gradient-stop-level">
            <label>
              <span>Selected stop</span>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={stop().level}
                disabled={props.disabled}
                onInput={(event) => updateActiveLevel(event.currentTarget.valueAsNumber, false)}
                onChange={(event) => updateActiveLevel(event.currentTarget.valueAsNumber, true)}
                aria-label="Gradient stop level"
              />
            </label>
            <label class="gradient-stop-number">
              <input
                type="number"
                min="0"
                max="100"
                step="1"
                value={Math.round(stop().level * 100)}
                disabled={props.disabled}
                onInput={(event) => updateActiveLevel(event.currentTarget.valueAsNumber / 100, false)}
                onChange={(event) => updateActiveLevel(event.currentTarget.valueAsNumber / 100, true)}
                aria-label="Gradient stop percent"
              /><i>%</i>
            </label>
          </div>
        )}
      </Show>
      <div class="gradient-presets" aria-label="Gradient presets">
        <button disabled={props.disabled} onClick={() => applyPreset('fade-in')}>Fade in</button>
        <button disabled={props.disabled} onClick={() => applyPreset('fade-out')}>Fade out</button>
        <button disabled={props.disabled} onClick={() => applyPreset('peak')}>Peak</button>
      </div>
      <button
        class="button button-primary gradient-apply"
        disabled={props.disabled || props.busy}
        onClick={() => props.onApply(props.stops, props.mirror)}
        data-testid={TEST_IDS.gradientApply}
      >
        {gradientActionLabel(props.applied, props.busy)}
      </button>
    </div>
  );
}
